import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, Clock, Target, Award } from "lucide-react";
import { FunnelBarChart, ProductPie, MonthlyTrendChart, BrokerRankingTable } from "../components/leads/FunnelCharts";
import AppShell from "../components/layout/AppShell";
import api from "../services/api";

var STATUS_LABELS = { novo: "Novo", qualificado: "Qualificado", proposta: "Proposta", negociacao: "Negociacao", fechado: "Fechado", perdido: "Perdido" };
var STATUS_COLORS_MAP = { novo: "#3B82F6", qualificado: "#8B5CF6", proposta: "#F59E0B", negociacao: "#F26A4B", fechado: "#10B981", perdido: "#EF4444" };
var PRODUCT_LABELS = { consorcio: "Consorcio", seguro: "Seguro", carta_contemplada: "Carta Contemplada" };
var SOURCE_LABELS = { indicacao: "Indicacao", site: "Site", telefone: "Telefone", rede_social: "Rede Social", direto: "Direto", outro: "Outro" };

function KPICard({ icon, label, value, sub, color }) {
  var Icon = icon;
  return (
    <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={"p-2.5 rounded-xl " + color}><Icon className="w-5 h-5 text-white" /></div>
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-xl lg:text-2xl font-bold text-[#0A2F3D]">{value}</p>
          {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

export default function LeadFunnelPage() {
  var dataState = useState(null);
  var loadingState = useState(true);

  useEffect(function() {
    api.get("/dashboard/lead-funnel").then(function(r) { dataState[1](r.data); }).catch(function() {}).finally(function() { loadingState[1](false); });
  }, []);

  if (loadingState[0]) return <AppShell activePage="funil-leads" title="Funil de Vendas"><div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div></AppShell>;
  var data = dataState[0];
  if (!data) return <AppShell activePage="funil-leads" title="Funil de Vendas"><p className="text-gray-500 text-center py-20">Erro ao carregar dados</p></AppShell>;

  var funnelOrder = ["novo", "qualificado", "proposta", "negociacao", "fechado", "perdido"];
  var statusMap = {};
  data.status_distribution.forEach(function(s) { statusMap[s.status] = s; });
  var funnelData = funnelOrder.map(function(s) { var d = statusMap[s] || { count: 0 }; return { name: STATUS_LABELS[s], count: d.count, fill: STATUS_COLORS_MAP[s] }; });
  var productData = data.product_distribution.map(function(p) { return { name: PRODUCT_LABELS[p.product_type] || p.product_type || "Outro", value: p.count }; });
  var sourceData = data.source_distribution.map(function(s) { return { name: SOURCE_LABELS[s.source] || s.source, value: s.count }; });
  var monthNames = ["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  var monthlyData = data.monthly_trend.map(function(m) { var p = m.month.split("-"); return { name: monthNames[parseInt(p[1], 10)], novos: m.novos, fechados: m.fechados, perdidos: m.perdidos }; });
  var fechados = (statusMap["fechado"] || {}).count || 0;
  var perdidos = (statusMap["perdido"] || {}).count || 0;

  return (
    <AppShell activePage="funil-leads" title="Funil de Vendas">
      <div data-testid="lead-funnel-page" className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="funnel-kpis">
          <KPICard icon={Target} label="Total de Leads" value={data.total_leads} color="bg-[#3B82F6]" />
          <KPICard icon={TrendingUp} label="Taxa de Conversao" value={data.conversion_rate + "%"} sub={fechados + " fechados"} color="bg-emerald-500" />
          <KPICard icon={TrendingDown} label="Taxa de Perda" value={data.loss_rate + "%"} sub={perdidos + " perdidos"} color="bg-red-500" />
          <KPICard icon={Clock} label="Tempo Medio" value={data.avg_conversion_days > 0 ? data.avg_conversion_days + "d" : "-"} sub="ate fechar" color="bg-amber-500" />
        </div>
        <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="funnel-bar-chart">
          <h3 className="text-base font-bold text-[#0A2F3D] mb-1">Funil de Vendas</h3>
          <p className="text-xs text-gray-400 mb-4">Distribuicao por etapa do funil</p>
          <FunnelBarChart data={funnelData} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="funnel-product-chart">
            <h3 className="text-base font-bold text-[#0A2F3D] mb-1">Por Produto</h3>
            <p className="text-xs text-gray-400 mb-4">Distribuicao de leads por produto</p>
            <ProductPie data={productData} />
          </div>
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="funnel-source-chart">
            <h3 className="text-base font-bold text-[#0A2F3D] mb-1">Por Origem</h3>
            <p className="text-xs text-gray-400 mb-4">De onde vem os leads</p>
            <ProductPie data={sourceData} />
          </div>
        </div>
        {monthlyData.length > 0 && (
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="funnel-monthly-chart">
            <h3 className="text-base font-bold text-[#0A2F3D] mb-1">Tendencia Mensal</h3>
            <p className="text-xs text-gray-400 mb-4">Ultimos 6 meses</p>
            <MonthlyTrendChart data={monthlyData} />
          </div>
        )}
        {data.broker_ranking.length > 0 && (
          <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm" data-testid="funnel-broker-ranking">
            <h3 className="text-base font-bold text-[#0A2F3D] mb-1 flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" />Ranking de Corretores</h3>
            <p className="text-xs text-gray-400 mb-4">Performance por corretor no funil</p>
            <BrokerRankingTable ranking={data.broker_ranking} />
          </div>
        )}
      </div>
    </AppShell>
  );
}
